import React, { useState } from 'react';
import { Calculator, DollarSign, TrendingUp, RotateCcw } from 'lucide-react';
import FbaFeeCalculator from '@/components/FbaFeeCalculator';
import CostCalculator from '@/components/CostCalculator';
import ProfitAnalyzer from '@/components/ProfitAnalyzer';
import UnitConverter from '@/components/UnitConverter';
import Button from '@/components/ui/button';

type TabType = 'fba' | 'cost' | 'profit' | 'converter';

export default function App() {
  const [activeTab, setActiveTab] = useState<TabType>('fba');

  const tabs = [
    {
      id: 'fba' as TabType,
      label: 'FBA费用计算',
      icon: Calculator,
      description: '计算亚马逊物流配送费用'
    },
    {
      id: 'cost' as TabType,
      label: '成本分析',
      icon: DollarSign,
      description: '分析产品成本和利润'
    },
    {
      id: 'profit' as TabType,
      label: '利润预测',
      icon: TrendingUp,
      description: '预测广告投放利润'
    },
    {
      id: 'converter' as TabType,
      label: '单位换算',
      icon: RotateCcw,
      description: '公制英制单位转换'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold gradient-text">
                Amazon FBA Calculator Pro
              </h1>
              <p className="mt-2 text-gray-600">
                专业的亚马逊FBA费用计算和成本分析工具 - 支持2026年最新费率
              </p>
            </div>
            <div className="hidden sm:block">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Fees updated:</span>
                <span className="font-semibold text-amazon-blue">Jan 2026</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto py-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200
                    whitespace-nowrap border border-transparent
                    ${activeTab === tab.id
                      ? 'bg-amazon-blue text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-gray-200'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-fade-in">
          {activeTab === 'fba' && <FbaFeeCalculator />}
          {activeTab === 'cost' && <CostCalculator />}
          {activeTab === 'profit' && <ProfitAnalyzer />}
          {activeTab === 'converter' && <UnitConverter />}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                功能特点
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 2026年最新FBA费率</li>
                <li>• 服装类商品专用计算</li>
                <li>• 危险品费用计算</li>
                <li>• 旺季/非旺季费率</li>
                <li>• 实时单位换算</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                费用类型
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 小号/大号标准尺寸</li>
                <li>• 小号/大件商品</li>
                <li>• 特殊超大尺寸</li>
                <li>• 体积重量计算</li>
                <li>• 低价产品折扣</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                技术支持
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 响应式设计</li>
                <li>• 离线功能支持</li>
                <li>• 数据自动保存</li>
                <li>• 结果导出功能</li>
                <li>• 持续更新维护</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <p className="text-sm text-gray-500">
                © 2026 Amazon FBA Calculator Pro. 基于2026年亚马逊官方费率表.
              </p>
              <div className="flex items-center space-x-4 mt-4 sm:mt-0">
                <span className="text-sm text-gray-500">
                  Version 2.0.0
                </span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-500">
                    实时更新
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}